/**
 * Timer.jsx
 *
 * Countdown timer component used in both Easy and Hard modes.
 *
 * Timer safety:
 * - Uses useRef to track interval ID so it's always cleanable
 * - Clears interval on unmount via cleanup return in useEffect
 * - Calls onExpire exactly once when time hits 0 (guarded by ref flag)
 * - The `locked` prop from parent prevents any answer submission after expiry
 * - Does NOT re-render on every tick — uses ref for smooth animation,
 *   only calls setState for the displayed number
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

const Timer = ({ duration, onExpire, isPaused, questionKey }) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const intervalRef = useRef(null);
  const expiredRef = useRef(false);
  const startTimeRef = useRef(null);

  // Reset timer whenever questionKey changes (new question)
  useEffect(() => {
    setTimeLeft(duration);
    expiredRef.current = false;
    startTimeRef.current = Date.now();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [questionKey, duration]);

  const handleExpire = useCallback(() => {
    if (!expiredRef.current) {
      expiredRef.current = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      onExpire();
    }
  }, [onExpire]);

  useEffect(() => {
    if (isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Use wall-clock time for accuracy (setInterval can drift)
    startTimeRef.current = Date.now() - (duration - timeLeft) * 1000;

    intervalRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const remaining = Math.max(0, duration - elapsed);
      const rounded = Math.ceil(remaining * 10) / 10;

      setTimeLeft(rounded);

      if (remaining <= 0) {
        handleExpire();
      }
    }, 50); // 50ms for smooth progress bar animation

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPaused, duration, handleExpire]); // eslint-disable-line react-hooks/exhaustive-deps

  const progress = timeLeft / duration;
  const isUrgent = timeLeft <= 2;
  const isCritical = timeLeft <= 1;

  return (
    <div className="timer-container">
      <div className="timer-bar-track">
        <div
          className={`timer-bar-fill ${isUrgent ? 'urgent' : ''} ${isCritical ? 'critical' : ''}`}
          style={{ width: `${progress * 100}%` }}
        />
      </div>
      <div className={`timer-display ${isUrgent ? 'urgent' : ''} ${isCritical ? 'critical' : ''}`}>
        {Math.ceil(timeLeft)}s
      </div>
    </div>
  );
};

export default Timer;
